import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  WritableSignal,
  signal,
  ChangeDetectorRef,
  AfterViewInit,
  NgZone,
  ViewEncapsulation,
  Inject,
  OnDestroy,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { PRIMECONTROL_BUTTONS } from 'app/shared/buttons/buttons';
import {
  TablePaginator,
  TABLE_PAGINATOR,
  PageEvent,
} from 'app/shared/table-paginator/table-paginator';
import { Pagination } from 'app/core/models/pagination.model';
import { TimeZone } from 'app/core/models/time-zone';
import { TimeZoneService } from 'app/core/services/time-zone/time-zone.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { PcDialogRef } from 'app/shared/dialog/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { ModalBody } from 'app/shared/molecules/modal/modal-body';
import { ModalFooter } from 'app/shared/molecules/modal/modal-footer';
import { InputField } from 'app/shared/atoms/input/input';
import { Button } from 'app/shared/atoms/button/button';
import { MainTable } from 'app/shared/organisms/main-table/main-table';
import { SortEvent, TableColumn } from 'app/shared/organisms/main-table/main-table.interface';

@Component({
  selector: 'primecontrol-time-zone',
  imports: [
    PRIMECONTROL_BUTTONS,
    ReactiveFormsModule,
    TABLE_PAGINATOR,
    TranslateModule,
    ModalBody,
    ModalFooter,
    InputField,
    Button,
    MainTable,
  ],
  templateUrl: './time-zone.html',
  styleUrls: ['./time-zone.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeZoneComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(TablePaginator) paginator: TablePaginator;
  @ViewChild(MainTable) mainTable: MainTable;

  public timeZones: WritableSignal<TimeZone[]> = signal([] as TimeZone[]);
  public selectedTimeZone: TimeZone | null = null;
  public columns: WritableSignal<TableColumn[]> = signal([]);
  public searchInputControl: UntypedFormControl = new UntypedFormControl();
  public sortOrder = 'asc';
  public sortColumn = '';
  public searchString = '';
  public pageNumber = 0;
  public pageSize = 10;
  public pagination;
  /** A fetch is in flight; drives the table's skeleton. */
  public isLoading: WritableSignal<boolean> = signal(false);
  /** The first fetch has gone out; later ones skip the app-wide splash. */
  private _hasLoadedOnce = false;
  /** The fetch in flight, cancelled by the next one. */
  private _listSubscription: Subscription | null = null;
  private searchSubscription: Subscription;

  constructor(
    private _timeZoneService: TimeZoneService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    @Inject('DATA') public _data: any,
    @Inject('CLOSE_DIALOG') private closeModal: (data: any) => void,
    @Inject('TITLE') private assignTitle: (title: string) => void
  ) {
    this.assignTitle('TIME_ZONE');
  }

  ngOnInit(): void {
    this.columns.set([
      { key: 'name', description: 'NAME' },
      { key: 'timezoneName', description: 'TIMEZONE' },
    ]);

    this.loadTimeZones();

    this.pagination = this._timeZoneService.pagination$;

    // Suscribirse a los cambios en el control de búsqueda con debounce
    this.searchSubscription = this.searchInputControl.valueChanges
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe((value) => {
        this.searchString = value;
        this.pageNumber = 0;
        this.loadTimeZones();
      });

    // Forzar la detección de cambios después de que se reciban los datos
    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    // Desuscribirse del control de búsqueda cuando se destruya el componente
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  /**
   * Fetches a page of time zones. The first fetch keeps the app-wide splash
   * (the picker is opening); a search, page or sort refetch runs behind the
   * table's skeleton, and cancels the one still in flight so a stale page
   * never lands last.
   */
  private loadTimeZones(): void {
    const background = this._hasLoadedOnce;
    this._hasLoadedOnce = true;
    this._listSubscription?.unsubscribe();
    this.isLoading.set(true);
    this._listSubscription = this._timeZoneService
      .getTimeZones(
        this.sortColumn,
        this.sortOrder,
        this.searchString,
        this.pageNumber,
        this.pageSize,
        background
      )
      .subscribe({
        next: (response) => {
          this.isLoading.set(false);
          this.timeZones.set(response.items);
          this.pagination = {
            pageIndex: response.pageIndex,
            totalPages: response.totalPages,
            totalItems: response.totalItems,
            hasPreviousPage: response.hasPreviousPage,
            hasNextPage: response.hasNextPage,
          };
          // Forzar la detección de cambios después de que se reciban los datos
          this.ngZone.run(() => {
            this.cdr.detectChanges();
          });
        },
        error: () => {
          this.isLoading.set(false);
          this.cdr.detectChanges();
        },
      });
  }

  changePage(event: PageEvent): void {
    this.pageNumber = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadTimeZones();
  }

  trackByFn(index: number, item: TimeZone): string {
    return item.name; // Utiliza el nombre como identificador único
  }

  onSort(event: SortEvent): void {
    this.sortColumn = event.column;
    this.sortOrder = event.order;
    this.pageNumber = 0;
    this.loadTimeZones();
  }

  /** Single-select picker: the clicked row becomes the choice (highlighted via
   *  main-table's selectedRow/rowIdKey) and ACCEPT returns it. Clicking the
   *  selected row again deselects it, which also disables ACCEPT again. */
  selectTimeZone(zone: TimeZone): void {
    this.selectedTimeZone = this.selectedTimeZone?.name === zone?.name ? null : zone;
    this.cdr.markForCheck();
  }

  close(data: any = false): void {
    this.closeModal(data);
  }
}
